import React from 'react';

export default function Footer() {
  // Scrolls smoothly to a page section identified by the given id.
  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn(`Footer: No element found with id "${id}".`);
      }
    } catch (error) {
      console.error('Footer: Unexpected error while scrolling:', error);
    }
  };

  return (
    <footer className="bg-[#0a1628] text-white">
      {/* Top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-700 via-blue-500 to-blue-700"></div>

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-14">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-droplet-fill text-white text-xl"></i>
              </div>
              <div>
                <span className="font-bold text-base tracking-wide leading-tight block">HIPERCLEAN DE AGUASCALIENTES S.A</span>
                <span className="text-blue-400 text-xs tracking-widest uppercase">Aguascalientes</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Proveedor industrial de insumos de limpieza, químicos especializados e higiénicos para empresas en la región centro de México.
            </p>
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-yellow-400 text-sm"></i>
                <i className="ri-star-fill text-yellow-400 text-sm"></i>
                <i className="ri-star-fill text-yellow-400 text-sm"></i>
                <i className="ri-star-fill text-yellow-400 text-sm"></i>
                <i className="ri-star-half-fill text-yellow-400 text-sm"></i>
              </div>
              <span className="text-white font-semibold text-sm">4.8/5</span>
              <span className="text-gray-500 text-xs">en Google</span>
            </div>
            <div className="flex gap-3">
              <a
                href="https://wa.me/524492037021"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-green-600 hover:bg-green-500 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-white text-base"></i>
              </a>
              <a
                href="tel:4499171543"
                className="w-9 h-9 bg-blue-700 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                aria-label="Llamar"
              >
                <i className="ri-phone-line text-white text-base"></i>
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-300 mb-6">
              Navegación
            </h3>
            <ul className="space-y-3">
              {[
                { id: 'inicio', label: 'Inicio' },
                { id: 'nosotros', label: 'Sobre Nosotros' },
                { id: 'productos', label: 'Líneas de Producto' },
                { id: 'sectores', label: 'Sectores que Atendemos' },
                { id: 'opiniones', label: 'Testimonios' },
                { id: 'faq', label: 'Preguntas Frecuentes' },
                { id: 'contacto', label: 'Solicitar Cotización' },
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="text-gray-400 hover:text-blue-400 transition-colors text-sm flex items-center gap-2 cursor-pointer group"
                  >
                    <span className="w-1 h-1 bg-blue-600 rounded-full group-hover:bg-blue-400 transition-colors flex-shrink-0"></span>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Product lines */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-300 mb-6">
              Líneas de Producto
            </h3>
            <ul className="space-y-3">
              {[
                { icon: 'ri-file-paper-line', label: 'Productos Higiénicos' },
                { icon: 'ri-flask-line', label: 'Químicos Industriales' },
                { icon: 'ri-test-tube-line', label: 'Alcoholes y Solventes' },
                { icon: 'ri-settings-3-line', label: 'Mezclas Personalizadas' },
                { icon: 'ri-tools-line', label: 'Suministros Industriales' },
                { icon: 'ri-leaf-line', label: 'Aromatización y Sanitización' },
              ].map((item) => (
                <li key={item.label} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-blue-500 text-sm`}></i>
                  </div>
                  <span className="text-gray-400 text-sm">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact info */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-300 mb-6">
              Contacto
            </h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-900/60 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line text-blue-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Dirección</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Camino San Bartolo 240<br />
                    Aguascalientes, México<br />
                    C.P. 20270
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-900/60 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-blue-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Teléfono</p>
                  <a href="tel:4499171543" className="text-gray-300 hover:text-blue-400 transition-colors text-sm font-medium cursor-pointer">
                    449 917 1543
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-900/60 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-blue-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Horario</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Lun – Vie: 9:00 AM – 5:00 PM<br />
                    Sáb: 9:00 AM – 2:00 PM
                  </p>
                </div>
              </li>
            </ul>

            <a
              href="https://wa.me/524492037021?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n%20empresarial"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base"></i>
              </div>
              Solicitar Cotización por WhatsApp
            </a>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs text-center md:text-left">
              &copy; 2025 <strong className="text-gray-400">HIPERCLEAN DE AGUASCALIENTES S.A</strong>. Todos los derechos reservados. Proveedor industrial de insumos de limpieza en la región centro de México.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-gray-600 hover:text-blue-400 transition-colors text-xs whitespace-nowrap cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
